import axiosInstance from "@/api/axios";
import { CancelTokenSource } from "axios";
import { configToUse } from "@/Keycloak";
import {
  AdCreative,
  AdCreativeExtraData,
  Folder,
  GenerateAdData,
  AccountAssets,
  PublishAdCreative,
  ImageEditHistoryResponse,
  ImageEditHistoryItem,
  CreativeWorkflow,
  VideoType,
  Moodboard,
  UgcVideoEditingPrompt,
  UgcVideoEditingPromptsResponse,
} from "./types";
import { Image } from "@/screens/creatives/types";

export const fetchCreativeCollection = async (
  accountId: string,
  folderId?: string,
  options?: { cancelToken?: CancelTokenSource },
) => {
  const res = await axiosInstance.post<{ data: AdCreative[]; status: string }>(
    `/creative-collection/${accountId}/explore/`,
    {
      folder_id: folderId,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: options?.cancelToken?.token,
    },
  );
  return res.data;
};

export const fetchCreative = async (
  accountId: string,
  creativeId: string,
  options?: { signal?: AbortSignal },
) => {
  const res = await axiosInstance.get<{ data: AdCreative; status: string }>(
    `/creative-collection/${accountId}/ad-creative/${creativeId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      signal: options?.signal,
    },
  );
  return res.data;
};

export const fetchMedia = async (
  accountId: string,
  mediaId: string,
  options?: { signal?: AbortSignal },
) => {
  const res = await axiosInstance.get<{ data: Image; status: string }>(
    `/creative-collection/${accountId}/media/${mediaId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      signal: options?.signal,
    },
  );
  return res.data;
};

// Hide a media item (soft-remove from views)
export const hideMedia = async (accountId: string, mediaId: string) => {
  const res = await axiosInstance.post<{ status: string }>(
    `/creative-collection/${accountId}/media/${mediaId}/hide/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

// Delete a media item (permanently remove)
export const deleteMedia = async (accountId: string, mediaId: string) => {
  const res = await axiosInstance.delete<{ status: string }>(
    `/creative-collection/${accountId}/media/${mediaId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const grabTextFromImage = async (
  accountId: string,
  imageId: string,
  options?: { signal?: AbortSignal },
) => {
  const res = await axiosInstance.post<{ data: Image; status: string }>(
    `/creative-collection/${accountId}/image/${imageId}/grab-text/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      signal: options?.signal,
    },
  );
  return res.data;
};

export const updateCreative = async (
  accountId: string,
  creativeId: string,
  folderId: string | null,
) => {
  const res = await axiosInstance.put<{ data: AdCreative; status: string }>(
    `/creative-collection/${accountId}/ad-creative/${creativeId}/`,
    {
      folder_id: folderId,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchFolders = async (accountId: string) => {
  const res = await axiosInstance.get<{ data: Folder[]; status: string }>(
    `/creative-collection/${accountId}/folders/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const createFolder = async (accountId: string, name: string) => {
  const res = await axiosInstance.post<{ data: Folder; status: string }>(
    `/creative-collection/${accountId}/folders/`,
    { name },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateFolder = async (accountId: string, folderId: string, name: string) => {
  const res = await axiosInstance.put<{ data: Folder; status: string }>(
    `/creative-collection/${accountId}/folder/${folderId}/`,
    { name },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteFolder = async (accountId: string, folderId: string) => {
  const res = await axiosInstance.delete<{ message: string; status: string }>(
    `/creative-collection/${accountId}/folder/${folderId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateExtraData = async (
  accountId: string,
  creativeId: string,
  extra_data: AdCreativeExtraData,
) => {
  const res = await axiosInstance.post<{ data: AdCreative; status: string }>(
    `/creative-collection/${accountId}/ad-creative/${creativeId}/update-extra-data/`,
    {
      extra_data,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchGenerateAdHistory = async (
  accountId: string,
  page_num: number = 1,
  page_size: number = 10,
  sort_type: "newest" | "oldest" = "newest",
  creative_type?: "image" | "video",
  options?: { signal?: AbortSignal },
) => {
  const res = await axiosInstance.get<{
    data: GenerateAdData[];
    status: string;
    pagination: {
      page_num: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }>(`/creative-collection/${accountId}/generate-ad/history/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    signal: options?.signal,
    params: {
      page_num,
      page_size,
      sort_type,
      ...(creative_type ? { creative_type } : {}),
    },
  });
  return res.data;
};

export const generateAd = async (
  accountId: string,
  data: {
    prompt: string;
    file_ids: string[];
    creative_type: "image" | "video";
    video_type?: VideoType;
    ugc_video_editing_prompt_id?: string | null;
    mask_file_id?: string;
    library_file_ids?: string[];
    aspect_ratio?: string | null;
    number_of_media?: number;
    avoid_danger_zone?: boolean;
    model?: "gpt-image-1" | "gpt-image-1.5" | "nano-banana-pro";
    moodboard_id?: string;
  },
) => {
  const res = await axiosInstance.post<{ data: GenerateAdData; status: string }>(
    `/creative-collection/${accountId}/generate-ad/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchUgcVideoEditingPrompts = async (data: {
  accountId: string;
  options?: { cancelToken?: CancelTokenSource };
}): Promise<UgcVideoEditingPromptsResponse> => {
  const res = await axiosInstance.get(
    `/creative-collection/${data.accountId}/ugc-video-editing-prompts/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.options?.cancelToken?.token,
    },
  );
  return res.data as UgcVideoEditingPromptsResponse;
};

export const createUgcVideoEditingPrompt = async (data: {
  accountId: string;
  prompt: string;
  options?: { cancelToken?: CancelTokenSource };
}): Promise<{ status: string; data: UgcVideoEditingPrompt }> => {
  const res = await axiosInstance.post(
    `/creative-collection/${data.accountId}/ugc-video-editing-prompts/`,
    { prompt: data.prompt },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.options?.cancelToken?.token,
    },
  );
  return res.data as { status: string; data: UgcVideoEditingPrompt };
};

export const updateUgcVideoEditingPrompt = async (data: {
  accountId: string;
  promptId: string;
  prompt: string;
  options?: { cancelToken?: CancelTokenSource };
}): Promise<{ status: string; data: UgcVideoEditingPrompt }> => {
  const res = await axiosInstance.put(
    `/creative-collection/${data.accountId}/ugc-video-editing-prompts/`,
    { id: data.promptId, prompt: data.prompt },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.options?.cancelToken?.token,
    },
  );
  return res.data as { status: string; data: UgcVideoEditingPrompt };
};

export const deleteUgcVideoEditingPrompt = async (data: {
  accountId: string;
  promptId: string;
  options?: { cancelToken?: CancelTokenSource };
}): Promise<{ status: string; message?: string }> => {
  const res = await axiosInstance.delete(
    `/creative-collection/${data.accountId}/ugc-video-editing-prompts/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.options?.cancelToken?.token,
      data: {
        id: data.promptId,
      },
    },
  );
  return res.data as { status: string; message?: string };
};

export const fetchMoodboards = async (
  accountId: string,
  options?: { signal?: AbortSignal },
) => {
  const res = await axiosInstance.get<{ status: string; data: Moodboard[] }>(
    `/creative-collection/${accountId}/moodboards/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      signal: options?.signal,
    },
  );
  return res.data;
};

// Best-effort helper to create a moodboard. If the backend doesn't support POST here,
// callers will receive the error and can fall back to navigating to a dedicated screen.
export const createMoodboard = async (accountId: string, name: string) => {
  const res = await axiosInstance.post<{ status: string; data: Moodboard }>(
    `/creative-collection/${accountId}/moodboards/`,
    { name },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const updateMoodboardName = async (
  accountId: string,
  moodboardId: string,
  name: string,
) => {
  const res = await axiosInstance.post<{ status: string; data: Moodboard }>(
    `/creative-collection/${accountId}/moodboard/${moodboardId}/`,
    { name },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const deleteMoodboard = async (accountId: string, moodboardId: string) => {
  const res = await axiosInstance.delete<{ status: string; message?: string }>(
    `/creative-collection/${accountId}/moodboard/${moodboardId}/`,
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const addImagesToMoodboard = async (
  accountId: string,
  moodboardId: string,
  files: File[],
) => {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  const res = await axiosInstance.post<{ status: string; files: Moodboard["files"] }>(
    `/creative-collection/${accountId}/moodboard/${moodboardId}/add-image/`,
    form,
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const removeImageFromMoodboard = async (
  accountId: string,
  moodboardId: string,
  file_id: string,
) => {
  const res = await axiosInstance.post<{ status: string; message?: string }>(
    `/creative-collection/${accountId}/moodboard/${moodboardId}/remove-image/`,
    { file_id },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const fetchWorkflowsList = async (
  accountId: string,
  page_num: number = 1,
  page_size: number = 10,
  sort_type: "newest" | "oldest" = "newest",
  options?: { signal?: AbortSignal },
) => {
  const res = await axiosInstance.get<{
    data: CreativeWorkflow[];
    status: string;
    pagination: {
      page_num: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }>(`/creative-collection/${accountId}/workflows/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    signal: options?.signal,
    params: {
      page_num,
      page_size,
      sort_type,
    },
  });
  return res.data;
};

export const fetchWorkflowById = async (
  accountId: string,
  workflowId: string,
  options?: { signal?: AbortSignal },
) => {
  const res = await axiosInstance.get<{
    status: string;
    data: {
      workflow: CreativeWorkflow;
    };
  }>(`/creative-collection/${accountId}/workflows/${workflowId}/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    signal: options?.signal,
  });
  return res.data;
};

export const createWorkflow = async (
  accountId: string,
  data: {
    workflow_name: string;
    prompt: string;
    library_file_ids: string[];
    file_ids: string[];
  },
) => {
  const res = await axiosInstance.post<{
    status: string;
    data: {
      workflow: CreativeWorkflow;
    };
  }>(`/creative-collection/${accountId}/workflows/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const retryWorkflow = async (accountId: string, workflowId: string) => {
  const res = await axiosInstance.post<{
    status: string;
    data?: {
      workflow: CreativeWorkflow;
    };
  }>(`/creative-collection/${accountId}/workflows/${workflowId}/retry/`, null, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const fetchAccountAssets = async (accountId: string) => {
  const res = await axiosInstance.get<{ data: AccountAssets; status: string }>(
    `/creative-collection/${accountId}/account-adsets/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const publishCreative = async (
  accountId: string,
  creativeId: string,
  adsetId: string,
) => {
  const res = await axiosInstance.post<{
    data: PublishAdCreative;
    status: string;
    message?: string;
  }>(
    `/creative-collection/${accountId}/ad-creative/${creativeId}/publish/`,
    { adset_id: adsetId },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updatePublishCreative = async (
  accountId: string,
  creativeId: string,
  adsetId: string,
) => {
  const res = await axiosInstance.put<{
    data: PublishAdCreative;
    status: string;
    message?: string;
  }>(
    `/creative-collection/${accountId}/ad-creative/${creativeId}/publish/`,
    { adset_id: adsetId },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchPublishCreativeList = async (
  accountId: string,
  params?: {
    state?: "unpublished" | "published";
    page_size?: number;
    cursor?: string | null;
  },
) => {
  const res = await axiosInstance.get<{
    data: PublishAdCreative[];
    status: string;
    pagination: {
      page_size: number;
      total_count: number;
      has_more: boolean;
      next_cursor: string | null;
    };
  }>(`/creative-collection/${accountId}/publish-creatives/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      ...(params?.state && { state: params.state }),
      ...(params?.page_size && { page_size: params.page_size }),
      ...(params?.cursor && { cursor: params.cursor }),
    },
  });
  return res.data;
};

export const fetchPublishAdCreative = async (
  accountId: string,
  publishAdCreativeId: string,
) => {
  const res = await axiosInstance.get<{
    data: AdCreative;
    status: string;
  }>(`/creative-collection/${accountId}/publish-creative/${publishAdCreativeId}/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const approvePublishAdCreative = async (
  accountId: string,
  publishCreativeId: string,
) => {
  const res = await axiosInstance.put<{
    data?: PublishAdCreative;
    message?: string;
    status: string;
  }>(
    `/creative-collection/${accountId}/publish-creative/${publishCreativeId}/approve/`,
    null,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const cancelPublishAdCreative = async (
  accountId: string,
  publishCreativeId: string,
) => {
  const res = await axiosInstance.delete<{ status: string; message: string }>(
    `/creative-collection/${accountId}/publish-creative/${publishCreativeId}/cancel/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const duplicateAdset = async (
  accountId: string,
  data: {
    adset_id: string;
    campaign_name: string;
    adset_name: string;
  },
) => {
  const res = await axiosInstance.post<{
    status: string;
    message?: string;
    data: {
      campaign_id: string;
      adset_id: string;
      campaign_name: string;
      adset_name: string;
    };
  }>(`/creative-collection/${accountId}/duplicate-adset/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const updateImageLogoPosition = async (
  accountId: string,
  imageId: string,
  data: {
    logo_file_id: string | null;
    logo_coordinates: {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    };
  },
) => {
  const res = await axiosInstance.put<{
    status: string;
    data: Image;
  }>(`/creative-collection/${accountId}/image/${imageId}/logo/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

// Add a new image to an ad creative with the requested aspect ratio

export const addImageToAdCreative = async (
  accountId: string,
  imageId: string,
  aspect_ratio: string,
  image_edit_id: string,
) => {
  const res = await axiosInstance.post<{
    status: string;
    message?: string;
    data: Image;
  }>(
    `/creative-collection/${accountId}/image/${imageId}/transform-image/`,
    { aspect_ratio, image_edit_id },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

// Fetch creative library items for an account
export const fetchCreativeLibrary = async (accountId: string) => {
  const res = await axiosInstance.get<{
    status: string;
    data: Array<{
      id: string;
      file_id: string | null;
      file_name: string;
      file_type: string;
      file_url: string;
      created_at: string;
      updated_at: string;
    }>;
  }>(`/creative-collection/${encodeURIComponent(accountId)}/creative-library-v2/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const createCreativeLibraryItem = async (accountId: string, file: File) => {
  const form = new FormData();
  form.append("file_data", file);
  // Derive and validate content_type (MIME)
  const allowedMimes: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/gif": "gif",
    "image/webp": "webp",
    "image/svg+xml": "svg",
    "image/bmp": "bmp",
    "image/tiff": "tiff",
  };
  const extToMime: Record<string, string> = Object.fromEntries(
    Object.entries(allowedMimes).map(([mime, ext]) => [ext, mime]),
  );
  let contentType = (file.type || "").toLowerCase();
  if (!allowedMimes[contentType]) {
    const name = (file.name || "").toLowerCase();
    const dot = name.lastIndexOf(".");
    const ext = dot >= 0 ? name.slice(dot + 1) : "";
    if (ext && extToMime[ext]) contentType = extToMime[ext];
  }
  if (contentType) form.append("content_type", contentType);
  const res = await axiosInstance.post<{
    status: string;
    data: {
      id: string;
      file_id: string | null;
      file_name: string;
      file_type: string;
      file_url: string;
      created_at: string;
      updated_at: string;
    };
  }>(`/creative-collection/${encodeURIComponent(accountId)}/creative-library-v2/`, form, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const deleteCreativeLibraryItem = async (accountId: string, itemId: string) => {
  const res = await axiosInstance.delete<{
    message?: string;
  }>(
    `/creative-collection/${encodeURIComponent(accountId)}/creative-library/${encodeURIComponent(itemId)}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

// Save edit configuration for an ad creative
export const updateEditConfiguration = async (
  accountId: string,
  creativeId: string,
  images: Array<{
    id: string;
    background_image_edit_id?: string;
    edit_configuration?: Record<string, unknown>;
    base64_data?: string | null;
  }>,
) => {
  const res = await axiosInstance.post<{
    status: string;
    data: AdCreative;
  }>(
    `/creative-collection/${accountId}/ad-creative/${creativeId}/update-edit-configuration/`,
    { images },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};
// Magic edit image
export const magicEditImage = async (
  accountId: string,
  imageId: string,
  data: {
    reference_image_edit_id: string;
    prompt: string;
    file_ids: string[];
    library_file_ids: string[];
  },
) => {
  const res = await axiosInstance.post<{
    status: string;
    data: ImageEditHistoryItem;
  }>(`/creative-collection/${accountId}/image/${imageId}/magic-edit/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

// Fetch image by id
export const fetchImageById = async (accountId: string, imageId: string) => {
  const res = await axiosInstance.get<{
    status: string;
    data: ImageEditHistoryItem;
  }>(`/creative-collection/${accountId}/image-edit/${imageId}/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

// Fetch image edit history
export const fetchImageEditHistory = async (
  accountId: string,
  imageId: string,
  page_size: number = 20,
  next_cursor?: string | null,
) => {
  const res = await axiosInstance.get<ImageEditHistoryResponse>(
    `/creative-collection/${accountId}/image/${imageId}/image-edit/history/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        page_size,
        ...(next_cursor && { next_cursor }),
      },
    },
  );
  return res.data;
};

// Stop magic edit
export const stopMagicEdit = async (accountId: string, imageEditId: string) => {
  const res = await axiosInstance.post<{
    status: string;
    message?: string;
  }>(
    `/creative-collection/${accountId}/image-edit/${imageEditId}/stop/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};
